/**
 * Tetrish - Asset Loader Utility
 * Handles preloading of game assets
 */

// Import image assets
import logoImg from '/assets/images/logo.png';
// import backgroundImg from '/assets/images/background.jpg';

/**
 * Preload all game assets
 */
export async function preloadAssets() {
	try {
		// Parallel loading of different asset types
		await Promise.all([
			preloadFonts(),
			preloadImages(),
			// Audio loading is handled by the SoundManager
		]);

		console.log('All assets preloaded successfully');
		return true;
	} catch (error) {
		console.error('Error preloading assets:', error);
		return false;
	}
}

/**
 * Preload web fonts using Web Font Loader
 */
async function preloadFonts() {
	return new Promise((resolve, reject) => {
		// Use Web Font Loader if available, otherwise resolve immediately
		if (typeof WebFont !== 'undefined') {
			WebFont.load({
				custom: {
					families: ['Space Grotesk', 'Inter'],
					urls: ['/assets/fonts/fonts.css']
				},
				active: resolve,
				inactive: () => {
					console.warn('Web fonts could not be loaded, using system fonts');
					resolve();
				},
				timeout: 3000 // 3 second timeout
			});
		} else {
			console.warn('WebFont loader not found, skipping font preload');
			resolve();
		}
	});
}

/**
 * Preload game images
 */
async function preloadImages() {
	const imageUrls = [
		logoImg,
	];

	// Create an array of image loading promises
	const imagePromises = imageUrls.map(url => loadImage(url));

	// Wait for all images to load
	return Promise.all(imagePromises);
}

/**
 * Load a single image
 */
function loadImage(url) {
	return new Promise((resolve, reject) => {
		const img = new Image();
		img.onload = () => resolve(img);
		img.onerror = () => {
			console.warn(`Failed to load image: ${url}`);
			resolve(null); // Resolve anyway to prevent Promise.all from failing
		};
		img.src = url;
	});
}

/**
 * Creates a font face observer for manual font loading without WebFont
 */
function createFontFaceObserver(fontFamily, { weight = 'normal', style = 'normal' } = {}) {
	const font = `${weight} ${style} 12px "${fontFamily}"`;
	const text = 'abcdefghijklmnopqrstuvwxyz0123456789';
	const canvas = document.createElement('canvas');
	const context = canvas.getContext('2d');

	function checkFont() {
		// Get the width with our font
		context.font = font;
		const metrics = context.measureText(text);
		const width = metrics.width;

		// Get the width with fallback font
		context.font = `${weight} ${style} 12px monospace`;
		const fallbackMetrics = context.measureText(text);
		const fallbackWidth = fallbackMetrics.width;

		// Different width likely means the custom font has loaded
		return width !== fallbackWidth;
	}

	return {
		load: function (timeout = 3000) {
			return new Promise((resolve, reject) => {
				const startTime = Date.now();

				function check() {
					if (checkFont()) {
						resolve();
						return;
					}

					if (Date.now() - startTime >= timeout) {
						reject(new Error(`Font load timeout: ${fontFamily}`));
						return;
					}

					setTimeout(check, 50);
				}

				check();
			});
		}
	};
}
